// src/contexts/geral/TokenContext.js

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMessage } from './MessageContext';
import { garantirSessaoAtiva as garantirSessaoAtivaApi } from '../../services/Api';
import { AUTH_SESSION_CHANGED_EVENT, AUTH_SESSION_EXPIRED_EVENT, getAuthSession } from '../../utils/AuthStorage';

const TokenContext = createContext();

const sessaoPodeSerRecuperada = (session) => {
    const agora = Date.now();
    const accessExpires = session.tokenExpires ? new Date(session.tokenExpires).getTime() : 0;
    const refreshExpires = session.refreshTokenExpires ? new Date(session.refreshTokenExpires).getTime() : 0;
    return Boolean(session.token) && (accessExpires > agora || refreshExpires > agora);
};

export const TokenProvider = ({ children }) => {
    const sessaoInicial = getAuthSession();
    const [token, setToken] = useState(sessaoInicial.token || null);
    const [tokenExpires, setTokenExpires] = useState(
        sessaoInicial.tokenExpires ? new Date(sessaoInicial.tokenExpires) : null
    );
    const [sessaoDisponivel, setSessaoDisponivel] = useState(sessaoPodeSerRecuperada(sessaoInicial));
    const [sessaoExpirada, setSessaoExpirada] = useState(false);
    const sessaoExpiradaRef = useRef(false);
    const { showMessage } = useMessage();

    const sincronizarSessao = useCallback(() => {
        const session = getAuthSession();
        setToken(session.token || null);
        setTokenExpires(session.tokenExpires ? new Date(session.tokenExpires) : null);
        setSessaoDisponivel(sessaoPodeSerRecuperada(session));
        setSessaoExpirada(false);
        sessaoExpiradaRef.current = false;
    }, []);

    const informarSessaoExpirada = useCallback(() => {
        setSessaoExpirada(true);

        if (!sessaoExpiradaRef.current) {
            sessaoExpiradaRef.current = true;
            showMessage(
                'Sua sessão expirou. Faça login novamente em outra aba e retorne para continuar sem perder os dados desta tela.',
                'warning'
            );
        }
    }, [showMessage]);

    const garantirSessaoAtiva = useCallback(() => {
        try {
            const resultado = garantirSessaoAtivaApi();

            if (resultado && typeof resultado.then === 'function') {
                return resultado
                    .then((tokenAtual) => {
                        setSessaoExpirada(false);
                        sessaoExpiradaRef.current = false;
                        return tokenAtual;
                    })
                    .catch((error) => {
                        informarSessaoExpirada();
                        throw error;
                    });
            }

            setSessaoExpirada(false);
            sessaoExpiradaRef.current = false;
            return resultado;
        } catch (error) {
            informarSessaoExpirada();
            throw error;
        }
    }, [informarSessaoExpirada]);

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === 'tokenGeradoAs' || event.key === null) {
                sincronizarSessao();
            }
        };
        const handleFocus = () => {
            if (!getAuthSession().token) return;

            try {
                const resultado = garantirSessaoAtiva();
                if (resultado && typeof resultado.catch === 'function') {
                    resultado.catch(() => {});
                }
            } catch (_) {
                // A mensagem de sessão expirada já foi apresentada pelo contexto.
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') handleFocus();
        };

        window.addEventListener(AUTH_SESSION_CHANGED_EVENT, sincronizarSessao);
        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, informarSessaoExpirada);
        window.addEventListener('storage', handleStorage);
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, sincronizarSessao);
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, informarSessaoExpirada);
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [garantirSessaoAtiva, informarSessaoExpirada, sincronizarSessao]);
    
    return (
        <TokenContext.Provider value={{
            token,
            setToken,
            tokenExpires,
            setTokenExpires,
            sessaoDisponivel,
            sessaoExpirada,
            garantirSessaoAtiva
        }}>
            {children}
        </TokenContext.Provider>
    );
};

export const useToken = () => {
    const context = useContext(TokenContext);
    if (!context) {
        throw new Error("useToken must be used within a TokenProvider");
    }
    return context;
};

